import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, RefreshCw, Clock, ExternalLink, ShieldCheck, Flag } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type Program = {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  apply_url: string | null;
  official_url: string | null;
  application_deadline: string | null;
  cycle_label: string | null;
  status: string;
  is_trending: boolean;
  last_verified_at: string | null;
  updated_at: string;
};

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function formatVerified(iso: string | null): string {
  if (!iso) return "Not yet verified";
  const d = daysSince(iso);
  if (d === null) return "Not yet verified";
  if (d <= 0) return "Verified today";
  if (d === 1) return "Verified 1 day ago";
  if (d < 30) return `Verified ${d} days ago`;
  const months = Math.floor(d / 30);
  return months === 1 ? "Verified 1 month ago" : `Verified ${months} months ago`;
}

function ProgramCard({ p, accent }: { p: Program; accent: string }) {
  const dleft = daysUntil(p.application_deadline);
  const link = p.apply_url || p.official_url;
  const [reporting, setReporting] = useState(false);

  const handleReport = async () => {
    if (reporting) return;
    setReporting(true);
    const { error } = await supabase.from("flagged_items").insert({
      program_id: p.id,
      reason: "user_reported" as never,
      details: `User-reported issue on "${p.title}" from homepage card.`,
    } as never);
    setReporting(false);
    if (error) {
      toast.error("Could not send report. Please try again.");
      return;
    }
    toast.success("Thanks — we'll review this listing.");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="h-full hover:shadow-md transition-shadow">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {p.category && <Badge variant="sector">{p.category}</Badge>}
            {p.status === "closing_soon" && (
              <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                Closing soon
              </Badge>
            )}
            {p.status === "next_cycle_expected" && (
              <Badge className="bg-sky-100 text-sky-800 border-sky-200">
                Next cycle expected
              </Badge>
            )}
          </div>
          <h3 className="font-semibold text-foreground mb-1 line-clamp-2">
            {p.title}
          </h3>
          {p.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {p.description}
            </p>
          )}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className={accent}>
              {dleft !== null && dleft >= 0
                ? `${dleft} day${dleft === 1 ? "" : "s"} left`
                : p.cycle_label || "Indicative"}
            </span>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Open
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2">
            <span
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
              title={
                p.last_verified_at
                  ? new Date(p.last_verified_at).toLocaleString()
                  : "Not yet verified by our team"
              }
            >
              <ShieldCheck className="h-3 w-3" />
              {formatVerified(p.last_verified_at)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReport}
              disabled={reporting}
              className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <Flag className="h-3 w-3 mr-1" />
              Report issue
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function Row({
  title,
  icon,
  programs,
  accent,
}: {
  title: string;
  icon: React.ReactNode;
  programs: Program[];
  accent: string;
}) {
  if (programs.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {programs.map((p) => (
          <ProgramCard key={p.id} p={p} accent={accent} />
        ))}
      </div>
    </div>
  );
}

export function DynamicProgramsSection() {
  const { data, isLoading } = useQuery({
    queryKey: ["home-dynamic-programs"],
    queryFn: async () => {
      const [trending, recent, closing] = await Promise.all([
        supabase
          .from("programs")
          .select("*")
          .eq("is_trending", true)
          .in("status", ["active", "closing_soon"])
          .order("updated_at", { ascending: false })
          .limit(6),
        supabase
          .from("programs")
          .select("*")
          .in("status", ["active", "closing_soon", "next_cycle_expected"])
          .order("last_verified_at", { ascending: false, nullsFirst: false })
          .limit(6),
        supabase
          .from("programs")
          .select("*")
          .eq("status", "closing_soon")
          .order("application_deadline", { ascending: true })
          .limit(6),
      ]);
      return {
        trending: (trending.data ?? []) as Program[],
        recent: (recent.data ?? []) as Program[],
        closing: (closing.data ?? []) as Program[],
      };
    },
    staleTime: 60_000,
  });

  // Hide entire section when nothing to show (e.g., before any programs are added)
  if (isLoading) return null;
  const total =
    (data?.trending.length ?? 0) +
    (data?.recent.length ?? 0) +
    (data?.closing.length ?? 0);
  if (total === 0) return null;

  return (
    <section className="py-12 bg-muted/30 border-y border-border">
      <div className="container space-y-10">
        <Row
          title="Closing Soon"
          icon={<Clock className="h-5 w-5 text-orange-600" />}
          programs={data!.closing}
          accent="text-orange-700 font-medium"
        />
        <Row
          title="Trending Programs"
          icon={<Flame className="h-5 w-5 text-rose-600" />}
          programs={data!.trending}
          accent="text-muted-foreground"
        />
        <Row
          title="Recently Updated"
          icon={<RefreshCw className="h-5 w-5 text-emerald-600" />}
          programs={data!.recent}
          accent="text-muted-foreground"
        />
      </div>
    </section>
  );
}